/* @flow */
import { uniqBy } from 'lodash';

import Store from 'lib/Store';
import { ActionTypes as GroupActionTypes } from 'groups/constants/GroupConstants';
import { ActionTypes as GroupMembershipActionTypes } from 'constants/GroupMembershipConstants';
import { ActionTypes as OrganizationActionTypes } from 'constants/OrganizationConstants';
import GroupMemberships from 'collections/GroupMemberships';
import GroupActions from 'groups/actions/GroupActions';
import Group from 'entities/Group';
import { PushEvents } from 'pusher/PusherConstants';
import { ActionTypes as RelationshipActionTypes } from 'constants/RelationshipConstants';
import { rootGroup } from 'entities/RootGroup';

class GroupMembershipStore extends Store {
  getInitialState() {
    return {
      members: new GroupMemberships(),
      isQueryComplete: {},
      hasBeenLoaded: {},
    };
  }

  isLoadedForGroup(groupUuid) {
    return !!this.state.hasBeenLoaded[groupUuid];
  }

  getGroupMembership(membershipId) {
    return this.state.members.get(membershipId);
  }

  getGroupMembershipsForGroup(groupId) {
    if (groupId === rootGroup.id) {
      return new GroupMemberships(uniqBy(this.state.members.toArray(), m => m.getUser().id));
    }
    return new GroupMemberships(this.state.members.filter(member => {
      return member.getGroupId() === groupId;
    }));
  }

  removeGroupMembershipsForGroup(groupId) {
    const membersToDelete = this.getGroupMembershipsForGroup(groupId);
    this.state.members.remove(membersToDelete);
  }

  allMembersLoadedForGroup(groupUuid: string, query: string = '') {
    const loaded = this.state.isQueryComplete;
    if (groupUuid in loaded) {
      return !!loaded[groupUuid][query];
    } else {
      return false;
    }
  }

  /* Copy over the related user of a relationship into
   * the group membership user object. This is useful when
   * the relation is updated (the name is confirmed, or the note updated)
   */
  handleRelationshipUpdate(relationship): boolean {
    if (!relationship.getSubscriptionsToMe().isEmpty()) {
      const userId = relationship.getSubscriptionsToMe().first().id;
      const member = this.getGroupMembership(userId);

      if (member) {
        /* We need to clone the membership list so that containers
         * know to rerender when performing a shallow comparison
         * of the otherwise unchanged backbone collection.
         */
        member.getUser().set(relationship.getRelatedUser().toJSON());
        this.state.members = this.state.members.clone();
        return true;
      }
    }
    return false;
  }

  handleDispatch(action: Object): void {
    switch (action.type) {
      case PushEvents.CHANGESET_CREATE_CLASSMEMBERSHIP: {
        // this assumes all classmembership update are for the same group.
        // which is the case currently with this push event
        const ids = action.changes.map(change => change.content.related_user_id);
        const group = Group.fromJSON({
          uuid: action.changes[0].content.group_uuid,
        });

        GroupActions.loadGroupMemberships(group, {
          perPage: ids.length,
          userIds: ids,
        });

        break;
      }
      case PushEvents.CHANGESET_DESTROY_CLASSMEMBERSHIP: {
        action.changes.forEach(change => this.state.members.remove(change.content.membership_id));
        this.emitChange();
        break;
      }
      case GroupMembershipActionTypes.LOAD_CLASS_MEMBERS: {
        this.handleLoadMembers(action.members);
        const uuid = action.group.getUuid() || 'r';
        this.state.hasBeenLoaded[uuid] = true;

        if (action.members.length < action.membersRequested) {
          if (!(uuid in this.state.isQueryComplete)) {
            this.state.isQueryComplete[uuid] = {};
          }
          this.state.isQueryComplete[uuid][action.query] = true;
        }

        this.emitChange();
        break;
      }
      case RelationshipActionTypes.UPDATE_RELATIONSHIP:
        if (this.handleRelationshipUpdate(action.relationship)) {
          this.emitChange();
        }
        break;
      case GroupMembershipActionTypes.PROMOTED_CLASS_MEMBERS:
        this.handleLoadMembers(action.members);
        this.emitChange();
        break;
      case GroupMembershipActionTypes.REMOVE_SUBSCRIBER: {
        const membership = this.state.members.find(m => m.getUser() === action.subscriber);
        this.state.members.remove(membership);
        this.emitChange();
        break;
      }
      case GroupActionTypes.LIST_GROUPS:
        action.groups.forEach(group => this.handleLoadMembers(group.getOwners()));
        this.emitChange();
        break;
      case GroupActionTypes.LOAD_GROUP:
      case GroupActionTypes.UPDATE_GROUP:
        this.handleLoadMembers(action.group.getOwners());
        this.emitChange();
        break;
      case GroupActionTypes.CREATE_GROUP:
      case GroupActionTypes.GROUPS_UPDATED:
        action.groups.forEach(group => this.handleLoadMembers(group.getOwners()));
        this.emitChange();
        break;
      case OrganizationActionTypes.UPDATE_ORG_AFF_VIEW:
        this.state.members.forEach(m => {
          if (m.getUser().id === action.userId) {
            m.set('org_affiliation_view', action.orgAffView);
          }
        });
        this.emitChange();
        break;
      case GroupActionTypes.CHANGE_ORG: {
        const { groupUuid } = action;
        this.state.isQueryComplete[groupUuid] = {};
        this.state.hasBeenLoaded[groupUuid] = false;
        this.state.members = new GroupMemberships(
          this.state.members.filter(m => m.getGroupId() !== action.groupId));
        this.emitChange();
        break;
      }
      default:
        break;
    }
  }

  handleLoadMembers(members) {
    members.forEach(member => this.state.members.add(member));
  }
}

export default new GroupMembershipStore();



// WEBPACK FOOTER //
// ./src/stores/GroupMembershipStore.js